import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, ChevronRight, Sparkles } from 'lucide-react';
import BundleDisclaimer from '../BundleDisclaimer';

/**
 * Post-submit success screen + smart cross-sell bundle.
 *
 * Renders nothing unless `submittedId` is truthy on the caller side; the
 * orchestrator early-returns this component instead of the multi-step UI.
 */
export default function SuccessScreen({ t, lang, onReset, crossSellBundle, disclaimerLabels }) {
  return (
    <div data-testid="configurator-success" className="max-w-2xl mx-auto">
      <div className="rounded-[24px] bg-white border border-[#E5E0D2] p-10 lg:p-16 text-center">
        <span className="inline-flex w-14 h-14 rounded-full bg-[#7FC242] text-white items-center justify-center mb-6">
          <Check className="w-7 h-7" strokeWidth={3} />
        </span>
        <h3 className="font-display text-[28px] lg:text-[36px] text-[#1F1D1A] leading-tight">{t.successTitle}</h3>
        <p className="mt-4 text-[#5C5852] text-[15px] leading-relaxed">{t.successSub}</p>
        <button
          type="button"
          onClick={onReset}
          data-testid="cfg-reset"
          className="mt-8 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1F1D1A] text-white text-[13px] font-semibold hover:bg-[#F26B1A] transition-colors"
        >
          {t.newConfig}
        </button>

        <BundleDisclaimer
          data-testid="cfg-success-disclaimer"
          labels={disclaimerLabels}
          variant="card"
          showWhiteStandard={false}
          title={lang === 'de' ? 'Hinweis zur Preisangabe' : 'Toelichting prijsopgave'}
          className="mt-8 text-left"
        />
      </div>

      {crossSellBundle && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          data-testid="cfg-crosssell"
          className="mt-8 relative overflow-hidden rounded-[24px] bg-[#1F1D1A] text-white p-7 lg:p-9 shadow-[0_30px_60px_-30px_rgba(31,29,26,0.45)]"
        >
          <span aria-hidden="true" className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-[#F26B1A]/30 blur-3xl" />
          <span aria-hidden="true" className="absolute -bottom-16 -left-10 w-44 h-44 rounded-full bg-[#7FC242]/20 blur-3xl" />
          <div className="relative">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F26B1A] text-white text-[10.5px] uppercase tracking-[0.20em] font-bold">
              <Sparkles className="w-3 h-3" /> {t.crossEyebrow}
            </span>
            <h4 className="mt-4 font-display text-[24px] lg:text-[30px] leading-tight">{t.crossTitle}</h4>
            <p className="mt-3 text-white/80 text-[14.5px] leading-relaxed max-w-md">
              {t.crossSubFmt
                .replace('{bundle}', crossSellBundle.name)
                .replace('{save}', crossSellBundle.displaySave)
                .replace('{pct}', String(crossSellBundle.savePct))}
            </p>
            <div className="mt-5 flex items-baseline gap-4 flex-wrap">
              <span className="font-display text-[36px] lg:text-[44px] leading-none tabular-nums">{crossSellBundle.displayPrice}</span>
              <span className="text-[14px] text-white/55 line-through tabular-nums">{crossSellBundle.displayBase}</span>
            </div>
            <Link
              to="/#bundles-section"
              onClick={() => {
                requestAnimationFrame(() => {
                  const target = document.querySelector('[data-testid="bundles-section"]');
                  if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                });
              }}
              data-testid="cfg-crosssell-cta"
              className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#F26B1A] text-white text-[13px] font-semibold hover:bg-[#D85A0A] transition-colors"
            >
              {t.crossCta} <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </motion.div>
      )}
    </div>
  );
}
